import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { Field, ErrorBox, Button, Loader } from 'components';

import messages from 'messages';

function createLabels(props) {
    return {
        borderBottom: props && props.withBorder ? '1px solid #b1b1b1' : 'none',
        padding: '0.5rem 0',
        margin: '0',
        top: '0',
    };
}

function Adyen3dCard({ history, callback, apiError, apiProcessing, ...props }) {
    const queryClient = useQueryClient();
    const { formatMessage } = props.intl;

    const payment = queryClient.getQueryData('payment') || {};

    const [cardNumber, setCardNumber] = useState({ empty: true, error: false });
    const [cardExpiryMonth, setCardExpiryMonth] = useState({ empty: true, error: false });
    const [cardCvc, setCardCvc] = useState({ empty: true, error: false });
    const [cardHolder, setCardHolder] = useState({ empty: true, error: false });

    const [encryptedCardNumber, setEncryptedCardNumber] = useState('');
    const [encryptedExpiryMonth, setEncryptedExpiryMonth] = useState('');
    const [encryptedExpiryYear, setEncryptedExpiryYear] = useState('');
    const [encryptedSecurityCode, setEncryptedSecutrityCode] = useState('');
    const [formData, setFormData] = useState('');
    const [encryptedCardHolder, setEncryptedCardHolder] = useState('');

    const [isLoading, setLoading] = useState(false);
    const [isReady, setReady] = useState(false);
    const [isUpdated, setUpdate] = useState(false);
    const [allValid, setAllValid] = useState(false);

    const [error] = useState({
        key: '',
        hasError: false,
        message: '',
    });

    const update = {
        encryptedCardNumber: setEncryptedCardNumber,
        encryptedExpiryMonth: setEncryptedExpiryMonth,
        encryptedExpiryYear: setEncryptedExpiryYear,
        encryptedSecurityCode: setEncryptedSecutrityCode,
    };

    useEffect(() => {
        if (!isUpdated) {
            setLoading(true);

            const origin = window.location.origin;
            const adyenOriginKey = payment?.keys?.keys[origin];

            const adyenConfig = {
                locale: 'en_US',
                environment: process.env.REACT_APP_ADYEN_ENV,
                originKey: adyenOriginKey,
                onChange: (st, cmpt) => {
                    setAllValid(st.isValid);
                    setFormData(st.data);
                },
            };

            if (window.AdyenCheckout) {
                const checkout = new window.AdyenCheckout(adyenConfig);
                checkout
                    .create('securedfields', {
                        type: 'card',
                        placeholders: {
                            encryptedCardNumber: formatMessage(messages.adyenLabelCardNumber),
                            encryptedExpiryMonth: formatMessage(messages.adyenLabelExpiryMonth),
                            encryptedExpiryYear: formatMessage(messages.adyenLabelExpiryYear),
                            encryptedSecurityCode: formatMessage(messages.adyenLabelCvc),
                        },
                        styles: {
                            base: {
                                fontSize: '13px',
                            },
                            placeholder: {
                                font: 'CircularStd',
                            },
                            error: {
                                color: '#CD5656',
                            },
                        },
                        ariaLabels: {
                            encryptedCardNumber: {
                                label: formatMessage(messages.adyenLabelCardNumber),
                            },
                            encryptedExpiryDate: {
                                label: formatMessage(messages.adyenLabelExpiry),
                            },
                            encryptedSecurityCode: { label: formatMessage(messages.adyenLabelCvc) },
                        },
                        onFieldValid: ({ encryptedFieldName: name, blob, ...rest }) => {
                            if (name === 'encryptedCardNumber') {
                                setCardNumber({ empty: false, error: false });
                            }
                            if (name === 'encryptedExpiryDate') {
                                setCardExpiryMonth({ empty: false, error: false });
                            }
                            if (name === 'encryptedSecurityCode') {
                                setCardCvc({ empty: false, error: false });
                            }

                            update[name](blob);
                        },
                        onError: ({ fieldType, error }) => {
                            if (fieldType === 'encryptedCardNumber') {
                                setCardNumber({ empty: false, error: !!error });
                            }
                            if (fieldType === 'encryptedExpiryDate') {
                                setCardExpiryMonth({ empty: false, error: !!error });
                            }
                            if (fieldType === 'encryptedSecurityCode') {
                                setCardCvc({ empty: false, error: !!error });
                            }
                        },
                        onLoad: ({ iframesLoaded }) => {
                            setReady(iframesLoaded);
                        },
                    })
                    .mount('#custom-adyen-form');

                setUpdate(true);
            }
        }
    }, [
        isLoading,
        isUpdated,
        update,
        encryptedCardHolder,
        encryptedCardNumber,
        encryptedExpiryMonth,
        encryptedExpiryYear,
        encryptedSecurityCode,
        formData,
    ]);

    const saveCard = (e) => {
        e.preventDefault();

        if (!allValid || !encryptedCardHolder) {
            return;
        }

        const data = {
            type: 'adyen',
            origin: window.location.origin,
            returnUrl: process.env.REACT_APP_BASE_API,
            merchantAccount: payment.merchantAccount,
            ...formData,
            paymentMethod: {
                ...formData.paymentMethod,
                holderName: encryptedCardHolder,
            },
        };

        callback(data);
    };

    return (
        <form id="custom-adyen-form" onSubmit={saveCard} className="adyen-form">
            <div className="content js-chckt-pm__pm-holder">
                {!isReady && <Loader />}

                <div style={{ visibility: isReady ? 'visible' : 'hidden' }}>
                    <input data-test="card-number" type="hidden" name="txvariant" value="card" />

                    <FieldGroup>
                        <AdyenField
                            label={formatMessage(messages.adyenLabelCardNumber)}
                            errorMessage={formatMessage(messages.adyenErrorCardNumber)}
                            {...cardNumber}
                            data-test="card-number"
                        >
                            <label style={createLabels()}>
                                <span
                                    data-test="card-number"
                                    id="card-number"
                                    data-cse="encryptedCardNumber"
                                />
                            </label>
                        </AdyenField>
                    </FieldGroup>

                    <FieldGroup width="45%">
                        <AdyenField
                            label={formatMessage(messages.adyenLabelExpiry)}
                            errorMessage={formatMessage(messages.adyenErrorCardExpiryDate)}
                            {...cardExpiryMonth}
                            data-test="card-month"
                        >
                            <label style={createLabels()}>
                                <span
                                    data-test="card-date"
                                    id="card-date"
                                    data-cse="encryptedExpiryDate"
                                />
                            </label>
                        </AdyenField>

                        <AdyenField
                            label={formatMessage(messages.adyenLabelCvc)}
                            errorMessage={formatMessage(messages.adyenErrorCardCvc)}
                            {...cardCvc}
                            data-test="card-cvc"
                        >
                            <label style={{ ...createLabels() }}>
                                <span
                                    data-test="card-cvv"
                                    id="card-cvc"
                                    data-cse="encryptedSecurityCode"
                                />
                            </label>
                        </AdyenField>
                    </FieldGroup>
                    <NameField
                        label={formatMessage(messages.adyenLabelNameOnCard)}
                        errorMessage={formatMessage(messages.adyenErrorCardHolder)}
                        {...cardHolder}
                    >
                        <label style={createLabels({ withBorder: true })}>
                            <input
                                data-test="card-holder-name"
                                value={encryptedCardHolder}
                                onChange={(e) => setEncryptedCardHolder(e.target.value)}
                                onKeyUp={(e) => {
                                    const value = e.target.value;
                                    const regexp = value
                                        ? /^[a-zA-Z\u00c0-\u017e\s]*$/.test(value)
                                        : true;
                                    setCardHolder({ empty: !value, error: !regexp });
                                }}
                                style={{ borderBottom: 'none', padding: '0' }}
                                placeholder={formatMessage(messages.adyenLabelNameOnCard)}
                            />
                        </label>
                    </NameField>
                </div>
            </div>
            <div style={{ visibility: isReady ? 'visible' : 'hidden' }}>
                <ErrorBox
                    hasError={error.hasError && error.key === 'system'}
                    message={error.message}
                />
                <ErrorBox
                    hasError={apiError.hasError && apiError.key === 'api'}
                    message={apiError.message}
                />

                <Button
                    data-test="card-button"
                    disabled={
                        apiProcessing ||
                        !encryptedCardHolder ||
                        !encryptedCardNumber ||
                        !encryptedExpiryMonth ||
                        !encryptedSecurityCode
                    }
                >
                    {apiProcessing
                        ? formatMessage(messages.genericLabelWait)
                        : formatMessage(messages.adyenButtonSave)}
                </Button>
            </div>
        </form>
    );
}

const NameField = styled(Field)`
    margin: 2.5rem 0;

    #field-label {
        top: 0;
    }

    #field-error {
        div {
            margin-top: 5px;
            width: 100%;
        }
    }
`;

const AdyenField = styled(Field)`
    font-size: 13px;
    margin: 2.5rem 0;

    #card-number,
    #card-date,
    #card-cvc {
        border-bottom: 1px solid rgb(177, 177, 177);
        color: #eee;
        font-size: 10px;
        float: left;
        height: 30px;
        padding: 0;
        position: relative;
        width: 100%;
    }

    #field-label {
        top: 0;
    }

    #field-error {
        div {
            width: 100%;
        }

        span {
            top: 2px;
            margin-right: 8px;
        }
    }

    label {
        color: rgb(177, 177, 177);
        display: block;
        margin-bottom: 0.2rem;
        text-align: left;
    }
`;

const FieldGroup = styled.div`
    -webkit-justify-content: space-between;
    display: flex;
    justify-content: space-between;
    height: 55px;
    margin: 30px 0 -15px;

    #field-wrapper {
        width: ${({ width }) => width || '100%'};
    }

    div {
        margin-top: 0;
        margin-bottom: 0;
    }
`;

export default injectIntl(React.memo(Adyen3dCard));
